import { getApiUrl } from './apiConfig';

export function formatClassDate(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse YYYY-MM-DD to a local Date (no timezone shift). */
export function classDateIsoToLocalDate(iso) {
  if (!iso) return new Date();
  const match = String(iso).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return new Date(iso);
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** Shift an ISO class date by a number of calendar days. */
export function shiftClassDateIso(iso, days) {
  const d = classDateIsoToLocalDate(iso);
  d.setDate(d.getDate() + Number(days) || 0);
  return formatClassDate(d);
}

export const EXTRA_CONTRIBUTION_REASONS = ['insurance', 'tshirts', 'hoodies', 'blocks'];

export function locationDisplayName(location) {
  return location?.Name ?? location?.name ?? `Location ${location?.id ?? ''}`;
}

export function formatClassMoney(amount, currency = 'EUR') {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  const cur = String(currency || 'EUR').trim().toUpperCase() || 'EUR';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(n);
  } catch {
    return `${cur} ${n.toFixed(2)}`;
  }
}

/** Signed balance: positive = owed, negative = credit/overpaid. */
export function formatSignedBalance(amount, currency = 'EUR') {
  const n = Number(amount);
  if (!Number.isFinite(n) || n === 0) return formatClassMoney(0, currency);
  const abs = formatClassMoney(Math.abs(n), currency);
  return n > 0 ? abs : `-${abs}`;
}

export function formatExtraContributionLabel(entry) {
  if (!entry) return '';
  return `(${formatClassMoney(entry.amount, entry.currency)} - ${entry.reason})`;
}

export function formatExtraContributionsSummary(contributions = []) {
  if (!Array.isArray(contributions) || contributions.length === 0) return '';
  return contributions.map((entry) => formatExtraContributionLabel(entry)).join(' ');
}

export async function fetchTrainingClassLocations() {
  const res = await fetch(`${getApiUrl()}/api/training-class-locations`);
  if (!res.ok) {
    throw new Error('Failed to load training class locations');
  }
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function createTrainingClassLocation(payload) {
  const res = await fetch(`${getApiUrl()}/api/training-class-locations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Failed to create class location');
  }
  return data;
}

export async function fetchClassBookSession(locationId, classDate) {
  const q = new URLSearchParams({
    training_class_location_id: String(locationId),
    class_date: classDate,
  });
  const res = await fetch(`${getApiUrl()}/api/class-book/session?${q.toString()}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Failed to load class session');
  }
  return {
    class: data.class ?? null,
    attendance: Array.isArray(data.attendance) ? data.attendance : [],
    fee_rate: data.fee_rate ?? null,
  };
}

export async function setClassBookNoClass({ locationId, classDate, noClass }) {
  const res = await fetch(`${getApiUrl()}/api/class-book/session/no-class`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      training_class_location_id: locationId,
      class_date: classDate,
      no_class: noClass === true,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Failed to update no-class flag');
  }
  return {
    class: data.class ?? null,
    attendance: Array.isArray(data.attendance) ? data.attendance : [],
    fee_rate: data.fee_rate ?? null,
  };
}

export async function syncClassBookAttendance({ locationId, classDate, studentNames }) {
  const res = await fetch(`${getApiUrl()}/api/class-book/session/attendance`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      training_class_location_id: locationId,
      class_date: classDate,
      student_names: studentNames,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Failed to save class attendance');
  }
  return {
    class: data.class ?? null,
    attendance: Array.isArray(data.attendance) ? data.attendance : [],
    fee_rate: data.fee_rate ?? null,
  };
}

export async function fetchStudentTallyAtLocation(locationId, studentName, options = {}) {
  const q = new URLSearchParams({ student_name: String(studentName || '').trim() });
  const { year, classDate } = options;
  if (year != null) {
    q.set('year', String(year));
  }
  if (classDate) {
    q.set('class_date', String(classDate));
  }
  const res = await fetch(
    `${getApiUrl()}/api/training-class-locations/${encodeURIComponent(String(locationId))}/student-tally?${q.toString()}`
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Failed to load student tally');
  }
  return data;
}

export async function fetchStudentBalanceAtLocation(locationId, studentName, options = {}) {
  const q = new URLSearchParams({ student_name: String(studentName || '').trim() });
  const { year, classDate } = options;
  if (year != null) {
    q.set('year', String(year));
  } else if (classDate) {
    q.set('class_date', String(classDate));
  }
  const res = await fetch(
    `${getApiUrl()}/api/training-class-locations/${encodeURIComponent(String(locationId))}/student-balance?${q.toString()}`
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Failed to load student balance');
  }
  return data;
}

export async function createClassPayment(payload) {
  const res = await fetch(`${getApiUrl()}/api/class-payments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Failed to record payment');
  }
  return data;
}

export async function createExtraContribution(payload) {
  const res = await fetch(`${getApiUrl()}/api/extra-contributions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Failed to record extra contribution');
  }
  return data;
}

export async function fetchTrainingClassLocation(locationId) {
  const res = await fetch(
    `${getApiUrl()}/api/training-class-locations/${encodeURIComponent(String(locationId))}`
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Failed to load class location');
  }
  return data;
}

export async function updateTrainingClassLocation(locationId, payload) {
  const res = await fetch(
    `${getApiUrl()}/api/training-class-locations/${encodeURIComponent(String(locationId))}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Failed to update class location');
  }
  return data;
}

export async function fetchLocationFeeRates(locationId) {
  const res = await fetch(
    `${getApiUrl()}/api/training-class-locations/${encodeURIComponent(String(locationId))}/fee-rates`
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Failed to load fee rates');
  }
  return Array.isArray(data) ? data : [];
}

export async function createLocationFeeRate(locationId, payload) {
  const res = await fetch(
    `${getApiUrl()}/api/training-class-locations/${encodeURIComponent(String(locationId))}/fee-rates`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Failed to save fee rate');
  }
  return data;
}

export async function recordClassAttendancePayment({
  locationId,
  studentName,
  attendanceId,
  amount,
  currency,
  paidAt,
}) {
  return createClassPayment({
    training_class_location_id: locationId,
    student_name: studentName,
    amount,
    currency,
    paid_at: paidAt,
    allocations: [{ class_attendance_id: attendanceId, amount }],
  });
}

export async function recordClassExtraContribution({
  locationId,
  studentName,
  attendanceId,
  amount,
  currency,
  reason,
  paidAt,
}) {
  return createExtraContribution({
    training_class_location_id: locationId,
    student_name: studentName,
    class_attendance_id: attendanceId,
    amount,
    currency,
    reason,
    paid_at: paidAt,
  });
}

/** @deprecated Fees are set in Class Builder, not Class Book */
export async function updateAttendanceLineFee(attendanceId, feeAmount) {
  const res = await fetch(
    `${getApiUrl()}/api/class-book/attendance/${encodeURIComponent(String(attendanceId))}/fee`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fee_amount: feeAmount }),
    }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Failed to update class fee');
  }
  return data;
}

/** @deprecated Use fetchClassBookSession */
export async function fetchClassForLocationAndDate(locationId, classDate) {
  const session = await fetchClassBookSession(locationId, classDate);
  return session.class;
}

/** @deprecated Use syncClassBookAttendance */
export async function upsertClassAttendance({ locationId, classDate, studentsAttended }) {
  const session = await syncClassBookAttendance({
    locationId,
    classDate,
    studentNames: studentsAttended,
  });
  return session.class;
}
